import { IMachineState } from '../interfaces/i-machine-state';
import { ITrajectory, ITrajektRefBreak } from '../interfaces/pitches';
import { ISpin, IVelocity } from '../interfaces/pitches/i-base';
import {
  ITrackmanPitch,
  ITrackmanRelease,
} from '../interfaces/trackman/i-ball';
import { BallHelper } from './ball.helper';
import { DEG_TO_RAD, FTPS_TO_MPH } from './math.utilities';
import { TrajHelper } from './trajectory.helper';

export class TrackmanHelper {
  // ! deprecate if we don't receive this from trackman
  // todo: is this local or global frame of ref?
  static get3DSpin = (rel: ITrackmanRelease): ISpin | undefined => {
    if (rel.SpinAxis3dLongitudinalAngle === undefined) {
      return undefined;
    }

    const longRAD = rel.SpinAxis3dLongitudinalAngle * DEG_TO_RAD;
    if (rel.SpinRate === undefined) {
      return undefined;
    }

    const activeSpin = rel.SpinRate * Math.cos(longRAD);
    if (rel.SpinAxis3dTransverseAngle === undefined) {
      return undefined;
    }

    const transRAD = rel.SpinAxis3dTransverseAngle * DEG_TO_RAD;

    const output: ISpin = {
      wx: activeSpin * Math.cos(transRAD),
      wy: rel.SpinRate * Math.sin(longRAD),
      wz: -activeSpin * Math.sin(transRAD),
    };

    return output;
  };

  static get3DSpeedMPH = (
    ms: IMachineState,
    data: ITrackmanPitch
  ): IVelocity => {
    const speedMPH = data.Release.Speed;

    const horRAD = (data.Release.HorizontalAngle ?? -ms.yaw) * DEG_TO_RAD;
    const vertRAD = (data.Release.VerticalAngle ?? ms.tilt) * DEG_TO_RAD;

    const output: IVelocity = {
      vx: -speedMPH * Math.sin(horRAD) * Math.cos(vertRAD),
      vy: -speedMPH * Math.cos(horRAD) * Math.cos(vertRAD),
      vz: speedMPH * Math.sin(vertRAD),
    };

    return output;
  };

  static getExtrapolatedTraj = (
    data: ITrackmanPitch,
    py: number
  ): ITrajectory => {
    // * Extrapolate given traj (nine parameter fit) to desired py release
    const spin = this.get3DSpin(data.Release);
    const wnet = spin ? BallHelper.getNetSpin(spin) : 0;

    const traj: ITrajectory = {
      px: data.NineP.X0.X,
      py: data.NineP.X0.Y,
      pz: data.NineP.X0.Z,
      vx: data.NineP.V0.X,
      vy: data.NineP.V0.Y,
      vz: data.NineP.V0.Z,
      ax: data.NineP.A0.X,
      ay: data.NineP.A0.Y,
      az: data.NineP.A0.Z,
      wnet: wnet,
    };

    const extrapolatedTraj = TrajHelper.extrapolateTrajectory(traj, py);

    return extrapolatedTraj;
  };

  static getExtrapolated3DSpeedMPH = (
    data: ITrackmanPitch,
    py: number
  ): IVelocity => {
    // * get release velocity from extrapolated traj
    const extrapolatedTraj = this.getExtrapolatedTraj(data, py);
    const extrapolatedVelo: IVelocity = {
      vx: extrapolatedTraj.vx * FTPS_TO_MPH,
      vy: extrapolatedTraj.vy * FTPS_TO_MPH,
      vz: extrapolatedTraj.vz * FTPS_TO_MPH,
    };

    return extrapolatedVelo;
  };

  static getExtrapolatedBreaks = (
    data: ITrackmanPitch,
    py: number
  ): ITrajektRefBreak => {
    // * get breaks from extrapolated traj
    const extrapolatedTraj = this.getExtrapolatedTraj(data, py);
    const extrapolatedBreaks = TrajHelper.getBreaks(extrapolatedTraj);

    return extrapolatedBreaks;
  };
}
